import {
  CaretLeft,
  CaretRight,
  Clock,
  Coins,
  DeviceMobile,
  FirstAidKit,
  NotePencil,
  Pause,
  Trophy,
} from '@phosphor-icons/react'
import clsx from 'clsx'
import { addDays, addWeeks } from 'date-fns'
import { EmblaCarouselType } from 'embla-carousel'
import useEmblaCarousel from 'embla-carousel-react'
import { shuffle } from 'lodash-es'
import Image from 'next/image'
import Link from 'next/link'
import { CSSProperties, JSX, useCallback, useEffect, useState } from 'react'
import { Website } from '@/src/@types/blinkadmin'
import { PageInfo } from '@/src/@types/models'
import { MyBlinkBenefitsBlock } from '@/src/@types/statamic'
import { Mandatory } from '@/src/@types/util'
import { getTeamMembers } from '@/src/AdminApiClient'
import LearnProgress from '@/src/blocks/MyBlinkBenefits/learn_progress.png'
import LearnmaterialLeft from '@/src/blocks/MyBlinkBenefits/learnmaterial_left.png'
import LearnmaterialMain from '@/src/blocks/MyBlinkBenefits/learnmaterial_main.png'
import LearnmaterialRight from '@/src/blocks/MyBlinkBenefits/learnmaterial_right.png'
import Button from '@/src/components/core/Button'
import CheckIcon from '@/src/components/core/icons/CheckIcon'
import { formatGerman, setWeekday } from '@/src/components/core/lib/date'
import Picture from '@/src/components/elements/Picture'
import { Block } from '@/src/lib/PageBuilder'

export async function getStaticProps({ locations }: MyBlinkBenefitsBlock) {
  const team = shuffle(
    await getTeamMembers(
      ['car'],
      locations?.map((location) => location.value)
    )
  )
  if (team.length < 6) {
    const rest = (await getTeamMembers(['car'], undefined)).filter(
      (member) => !member.short_description.toLowerCase().includes('partner')
    )
    team.push(...rest)
  }

  const mainInstructor = team[0]
  let restInstructors = team
  if (team[0].short_description.toLowerCase().includes('partner')) {
    const nonPartnerInstructor = team.find(
      (member) => !member.short_description.toLowerCase().includes('partner')
    )
    if (nonPartnerInstructor) {
      restInstructors = [
        nonPartnerInstructor,
        ...team.filter((member) => member.slug !== nonPartnerInstructor.slug),
      ]
    }
  }

  return {
    instructors: [mainInstructor, ...restInstructors],
  }
}

export default function Component({
  instructors,
  blocks,
  index,
  pageInfo,
}: Awaited<ReturnType<typeof getStaticProps>> & {
  blocks: Block[]
  index: number
  pageInfo: PageInfo
}) {
  const removeTopMargin = blocks[index - 1]?.block === 'cta_banner'
  const [emblaRef, emblaApi] = useEmblaCarousel({
    skipSnaps: true,
  })
  const [prevDisabled, setPrevDisabled] = useState(true)
  const [nextDisabled, setNextDisabled] = useState(true)
  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setPrevDisabled(!emblaApi.canScrollPrev())
    setNextDisabled(!emblaApi.canScrollNext())
  }, [])

  useEffect(() => {
    if (!emblaApi) return

    onSelect(emblaApi)
    emblaApi.on('reInit', onSelect).on('select', onSelect)
  }, [emblaApi, onSelect])

  const prev = useCallback(() => {
    if (!emblaApi) return
    emblaApi?.plugins()?.autoplay?.stop()
    emblaApi.scrollPrev()
  }, [emblaApi])

  const next = useCallback(() => {
    if (!emblaApi) return
    emblaApi?.plugins()?.autoplay?.stop()
    emblaApi.scrollNext()
  }, [emblaApi])

  return (
    <div
      className={clsx(
        'bg-linear-to-b from-gray-50 to-gray-100 pt-16 pb-16 md:pt-32 md:pb-32',
        {
          '-mt-20 lg:-mt-24': removeTopMargin,
          'mt-20 md:mt-32':
            pageInfo.newSpacing && blocks[index - 1]?.block !== 'trial_lesson',
        }
      )}
    >
      <div className="mx-auto flex max-w-container items-center justify-between px-5 md:px-6">
        <h2 className="text-4xl font-black md:text-20xl">
          Deine Vorteile mit myBLINK
        </h2>
        <Link
          href="https://myblink.blinkdrive.ch"
          target="_blank"
          className="ml-5 hidden font-bold whitespace-nowrap text-purple-600 md:block"
        >
          Zum myBLINK wechseln
        </Link>
      </div>
      <div className="mt-6 overflow-hidden pb-10 md:mt-10" ref={emblaRef}>
        <ul className="flex select-none">
          <Benefit
            illustration={() => IllustrationOnlineBooking(instructors[0])}
            title="Alles online buchen."
            description="Fahrstunden, VKU oder Nothelferkurs: Bei uns buchst du deine Termine direkt im myBLINK."
          />
          <Benefit
            illustration={IllustrationFinance}
            title="Finanzen im Griff."
            description="Behalte den Überblick über deine Ausgaben und bezahle easy online mit TWINT, Kreditkarte oder per Rechnung."
          />
          <Benefit
            illustration={IllustrationClearPlan}
            title="Klarer Plan."
            description="Du weisst immer, wo du stehst und was deine nächsten Schritte sind. So kommst du schnell und direkt zu deinem Führerschein!"
          />
          <Benefit
            illustration={IllustrationLearningMaterials}
            title="Umfassendes Lernmaterial."
            description="Mit unseren Videos und Unterlagen kannst du auch ausserhalb der Fahrlektionen effizient lernen."
          />
          <Benefit
            illustration={IllustrationLearnProgress}
            title="Lernfortschritte auf einen Blick."
            description="Nach jeder Fahrlektion siehst du, in welchen Punkten du schon prüfungsreif bist und wo du noch gezielt üben musst."
          />
          <Benefit
            illustration={() =>
              IllustrationChooseInstructor(instructors.slice(1))
            }
            title="No Drama."
            description="Du kannst jederzeit deine:n BLINK-Fahrlehrer:in wechseln oder einfach mehrere ausprobieren."
          />
        </ul>
      </div>
      <div className="mx-auto flex max-w-container items-center justify-between px-5 md:justify-end md:px-6">
        <Link
          href="https://myblink.blinkdrive.ch"
          target="_blank"
          className="font-bold whitespace-nowrap text-purple-600 md:hidden"
        >
          Zum myBLINK wechseln
        </Link>
        <div className="space-x-3">
          <Button
            size="icon-small"
            icon={<CaretLeft weight="bold" />}
            onClick={prev}
            version="gray-outlined"
            disabled={prevDisabled}
          />
          <Button
            size="icon-small"
            icon={<CaretRight weight="bold" />}
            onClick={next}
            version="gray-outlined"
            disabled={nextDisabled}
          />
        </div>
      </div>
    </div>
  )
}

const Benefit = ({
  illustration,
  title,
  description,
}: {
  illustration: () => JSX.Element
  title: string
  description: string
}) => {
  return (
    <li className="ml-5 min-w-0 flex-[0_0_calc(100%-40px-20px)] last:mr-5 min-[1342px]:first:ml-[calc((100%-1280px)/2)] min-[1342px]:last:mr-[calc((100%-1280px)/2)]! md:ml-6 md:flex-[0_0_405px] md:last:mr-6">
      <div
        className="@container aspect-square w-full overflow-hidden rounded-xl border border-gray-200 bg-gray-25"
        style={
          {
            '--spacing': 'calc(100cqw/85)',
            '--one-px': 'calc(var(--spacing)/4)',
            '--one-rem': 'calc(var(--spacing)*4)',
            '--text-2xs': 'calc(var(--one-rem)*0.625)',
            '--text-2xs--line-height': 'calc(var(--one-rem)*1)',
            '--text-xs': 'calc(var(--one-rem)*0.75)',
            '--text-xs--line-height': 'calc(var(--one-rem)*1)',
            '--text-sm': 'calc(var(--one-rem)*0.875)',
            '--text-sm--line-height': 'calc(var(--one-rem)*1.25)',
            '--text-base': 'var(--one-rem)',
            '--text-base--line-height': 'calc(var(--one-rem)*1.5)',
            '--text-lg': 'calc(var(--one-rem)*1.125)',
            '--text-lg--line-height': 'calc(var(--one-rem)*1.75)',
            '--text-xl': 'calc(var(--one-rem)*1.25)',
            '--text-xl--line-height': 'calc(var(--one-rem)*1.75)',
            '--text-2xl': 'calc(var(--one-rem)*1.375)',
            '--text-2xl--line-height': 'calc(var(--one-rem)*1.875)',
            '--text-4xl': 'calc(var(--one-rem)*1.625)',
            '--text-4xl--line-height': 'calc(var(--one-rem)*2)',
            '--text-7xl': 'calc(var(--one-rem)*2)',
            '--text-7xl--line-height': 'calc(var(--one-rem)*2.5)',
            '--text-10xl': 'calc(var(--one-rem)*2.375)',
            '--text-10xl--line-height': 'calc(var(--one-rem)*3)',
            '--text-20xl': 'calc(var(--one-rem)*3)',
            '--text-20xl--line-height': 'calc(var(--one-rem)*3.5)',
            '--text-25xl': 'calc(var(--one-rem)*3.5)',
            '--text-25xl--line-height': 'calc(var(--one-rem)*4)',
            '--text-30xl': 'calc(var(--one-rem)*4)',
            '--text-30xl--line-height': 'calc(var(--one-rem)*4.5)',
            '--text-35xl': 'calc(var(--one-rem)*4.5)',
            '--text-35xl--line-height': 'calc(var(--one-rem)*5)',
            '--radius-xs': 'calc(var(--spacing)*0.5)',
            '--radius-sm': 'var(--spacing)',
            '--radius-md': 'calc(var(--spacing)*1.5)',
            '--radius-lg': 'calc(var(--spacing)*2)',
            '--shadow-xs':
              '0 calc(var(--one-px) * 1) calc(var(--one-px) * 2) 0 rgb(0 0 0 / 0.05)',
            '--shadow-smooth':
              'calc(var(--one-px) * 9) calc(var(--one-px) * 25) calc(var(--one-px) * 7) 0 rgba(28, 32, 61, 0.00), calc(var(--one-px) * 6) calc(var(--one-px) * 16) calc(var(--one-px) * 7) 0 rgba(28, 32, 61, 0.01), calc(var(--one-px) * 3) calc(var(--one-px) * 9) calc(var(--one-px) * 6) 0 rgba(28, 32, 61, 0.03), calc(var(--one-px) * 1) calc(var(--one-px) * 4) calc(var(--one-px) * 4) 0 rgba(28, 32, 61, 0.05), 0 calc(var(--one-px) * 1) calc(var(--one-px) * 2) 0 rgba(28, 32, 61, 0.06)',
            '--shadow-inner':
              'calc(var(--one-px) * -1) calc(var(--one-px) * 2) calc(var(--one-px) * 6) 0 rgba(0, 0, 0, 0.30) inset',
          } as CSSProperties
        }
      >
        {illustration()}
      </div>
      <h3 className="mt-5 text-xl font-black md:text-2xl">{title}</h3>
      <p className="mt-2">{description}</p>
    </li>
  )
}

const IllustrationOnlineBooking = (
  instructor: Mandatory<Website.TeamMember, 'image'>
) => {
  const [nextWednesday, setNextWednesday] = useState<Date | null>(null)

  useEffect(() => {
    let nextWednesday = new Date()
    nextWednesday = addWeeks(nextWednesday, 1)
    nextWednesday = setWeekday(nextWednesday, 2)
    setNextWednesday(nextWednesday)
  }, [])

  return (
    <div className="relative text-base">
      <div className="mt-5 grid w-121 -translate-x-27 grid-cols-4 divide-x divide-gray-150">
        <div className="px-2 opacity-40">
          <div className="h-18" />
          <div className="space-y-3.5">
            <div className="h-12 rounded-sm [border-width:var(--one-px)] border-gray-300 bg-gray-50" />
            <div className="h-12 rounded-sm [border-width:var(--one-px)] border-gray-300 bg-gray-50" />
            <div className="h-12 rounded-sm [border-width:var(--one-px)] border-gray-300 bg-gray-50" />
            <div className="h-12 rounded-sm [border-width:var(--one-px)] border-gray-300 bg-gray-50" />
          </div>
        </div>
        <div className="px-2">
          <div className="flex flex-col items-center">
            <p className="mt-1 text-gray-600">Mi</p>
            <p className="mt-1 font-black text-gray-800">
              {nextWednesday ? formatGerman(nextWednesday, 'dd.MM') : 'xx.xx'}
            </p>
          </div>
          <div className="mt-3 space-y-3.5">
            <div className="relative flex h-12 items-center justify-center rounded-sm bg-linear-to-b from-purple-600 to-purple-700 shadow-(--shadow-inner)">
              <p className="text-center text-sm font-bold text-white">09:00</p>
              <svg
                viewBox="0 0 94 48"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="absolute inset-0 size-full opacity-30"
              >
                <g style={{ mixBlendMode: 'soft-light' }}>
                  <path
                    d="M11.9128 75.1022L45.1265 -15.3116L54.0465 -15.6107L21.8822 74.7679L11.9128 75.1022Z"
                    fill="url(#paint0_linear_12794_122529)"
                    fill-opacity="0.2"
                  />
                  <path
                    d="M13.477 110.475L68.2954 -39.4966L83.0238 -39.9905L29.9381 109.923L13.477 110.475Z"
                    fill="url(#paint1_linear_12794_122529)"
                    fill-opacity="0.2"
                  />
                </g>
                <defs>
                  <linearGradient
                    id="paint0_linear_12794_122529"
                    x1="31.4842"
                    y1="-14.8542"
                    x2="34.4751"
                    y2="74.3457"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop offset="0.177885" stop-color="#FBFCFE" />
                    <stop offset="0.576923" stop-color="#F6F8FD" />
                    <stop offset="1" stop-color="#EFF2FB" />
                  </linearGradient>
                  <linearGradient
                    id="paint1_linear_12794_122529"
                    x1="45.7697"
                    y1="-38.7413"
                    x2="50.7311"
                    y2="109.226"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop offset="0.177885" stop-color="#FBFCFE" />
                    <stop offset="0.576923" stop-color="#F6F8FD" />
                    <stop offset="1" stop-color="#EFF2FB" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
            <div className="flex h-12 items-center justify-center rounded-sm [border-width:var(--one-px)] border-gray-300 bg-gray-50">
              <p className="text-center text-sm font-bold">11:00</p>
            </div>
            <div className="flex h-12 items-center justify-center rounded-sm [border-width:var(--one-px)] border-gray-300 bg-gray-50">
              <p className="text-center text-sm font-bold">14:20</p>
            </div>
          </div>
        </div>
        <div className="px-2">
          <div className="flex flex-col items-center opacity-30">
            <p className="mt-1 text-gray-600">Do</p>
            <p className="mt-1 font-black text-gray-800">
              {nextWednesday
                ? formatGerman(addDays(nextWednesday, 1), 'dd.MM')
                : 'xx.xx'}
            </p>
          </div>
          <div className="mt-3 space-y-3.5 opacity-40">
            <div className="h-12 rounded-sm [border-width:var(--one-px)] border-gray-300 bg-gray-50" />
            <div className="h-12 rounded-sm [border-width:var(--one-px)] border-gray-300 bg-gray-50" />
            <div className="h-12 rounded-sm [border-width:var(--one-px)] border-gray-300 bg-gray-50" />
            <div className="h-12 rounded-sm [border-width:var(--one-px)] border-gray-300 bg-gray-50" />
          </div>
        </div>
        <div className="px-2">
          <div className="flex flex-col items-center opacity-30">
            <p className="mt-1 text-gray-600">Fr</p>
            <p className="mt-1 font-black text-gray-800">
              {nextWednesday
                ? formatGerman(addDays(nextWednesday, 2), 'dd.MM')
                : 'xx.xx'}
            </p>
          </div>
          <div className="mt-3 space-y-3.5 opacity-40">
            <div className="h-12 rounded-sm [border-width:var(--one-px)] border-gray-300 bg-gray-50" />
            <div className="h-12 rounded-sm [border-width:var(--one-px)] border-gray-300 bg-gray-50" />
            <div className="h-12 rounded-sm [border-width:var(--one-px)] border-gray-300 bg-gray-50" />
            <div className="h-12 rounded-sm [border-width:var(--one-px)] border-gray-300 bg-gray-50" />
          </div>
        </div>
      </div>
      <div className="absolute top-8 left-33 w-75 rounded-lg [border-width:var(--one-px)] border-gray-300 bg-white shadow-(--shadow-smooth)">
        <div className="[border-bottom-width:var(--one-px)] border-gray-150 px-4 pt-3 pb-2">
          <p className="text-sm">Termin auswählen</p>
        </div>
        <div className="flex items-center px-4 py-4">
          <div className="relative size-11 shrink-0">
            <Picture
              permalink={instructor.image}
              alt={instructor.name}
              fill
              sizes="200px"
              className="rounded-full object-cover object-top"
            />
          </div>
          <div className="ml-3">
            <p className="font-black">{instructor.name}</p>
            <p className="mt-0.5 text-xs text-gray-600">
              {nextWednesday
                ? formatGerman(nextWednesday, 'dd.MM.yyyy')
                : 'xx.xx.xxxx'}
              , 09:00 Uhr
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

const IllustrationFinance = () => {
  const [inOneMonth, setInOneMonth] = useState<Date | null>(null)

  useEffect(() => {
    let inOneMonth = new Date()
    inOneMonth = addDays(inOneMonth, 30)
    setInOneMonth(inOneMonth)
  }, [])

  return (
    <div className="flex size-full flex-col justify-between text-base">
      <div>
        <p className="mt-8 text-center text-gray-600">Betrag</p>
        <p className="mt-1 bg-linear-to-b from-gray-600 to-gray-700 bg-clip-text text-center text-10xl font-black text-transparent">
          CHF 59
        </p>
        <div className="flex justify-center">
          <div className="mt-2 flex items-center">
            <Clock weight="fill" className="size-5 text-gray-600" />
            <p className="ml-1 text-sm">
              zahlbar bis{' '}
              {inOneMonth
                ? formatGerman(inOneMonth, 'dd.MM.yyyy')
                : 'xx.xx.xxxx'}
            </p>
          </div>
        </div>
      </div>
      <div className="mx-4 rounded-t-lg [border-inline-width:var(--one-px)] [border-top-width:var(--one-px)] border-gray-150 bg-white px-1 pt-1 shadow-(--shadow-smooth)">
        <div className="flex items-center justify-between rounded bg-purple-50 p-3">
          <div className="flex items-center space-x-3">
            <div className="flex size-5 items-center justify-center rounded-full bg-purple-600">
              <div className="size-2 rounded-full bg-white" />
            </div>
            <div>
              <p className="text-sm">BLINK Guthaben</p>
              <p className="text-2xs text-gray-600">CHF 72 verfügbar</p>
            </div>
          </div>
          <div className="flex items-center justify-center rounded-xs [border-width:var(--one-px)] border-gray-150 bg-white px-3 py-1">
            <Coins weight="bold" className="size-6 text-yellow-600" />
          </div>
        </div>
        <div className="flex items-center justify-between p-3">
          <div className="flex items-center space-x-3">
            <div className="flex size-5 items-center justify-center rounded-full bg-purple-600">
              <div className="size-4 rounded-full bg-white" />
            </div>
            <p className="text-sm">TWINT</p>
          </div>
          <div className="h-8 w-12 rounded-xs bg-gray-50" />
        </div>
        <div className="flex items-center justify-between p-3">
          <div className="flex items-center space-x-3">
            <div className="flex size-5 items-center justify-center rounded-full bg-purple-600">
              <div className="size-4 rounded-full bg-white" />
            </div>
            <p className="text-sm">Kreditkarte</p>
          </div>
          <div className="h-8 w-12 rounded-xs bg-gray-50" />
        </div>
      </div>
    </div>
  )
}

const IllustrationClearPlan = () => {
  return (
    <div className="flex size-full flex-col items-center justify-center px-5 text-base">
      <div className="w-full px-2">
        <div className="relative flex w-full items-center rounded-lg [border-width:var(--one-px)] border-gray-150 bg-white p-3 opacity-80 shadow-(--shadow-xs)">
          <div className="flex size-11 items-center justify-center rounded-full [border-width:var(--one-px)] border-gray-150 bg-gray-100">
            <FirstAidKit weight="fill" className="size-5 text-gray-600" />
          </div>
          <div className="ml-3">
            <p className="text-sm font-black">Nothilfekurs</p>
            <p className="text-xs text-gray-600">Absolviere den Nothilfekurs</p>
          </div>
          <p className="absolute top-1.5 right-2.5 text-2xs text-gray-600">
            Abgeschlossen
          </p>
        </div>
      </div>
      <svg
        viewBox="0 0 4 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="h-6 w-1"
      >
        <path
          d="M0 21C-8.20931e-10 19.9568 0.845468 19.1113 1.88867 19.1113C2.93188 19.1113 3.77734 19.9568 3.77734 21V24H0V21ZM0 15V9C-8.20931e-10 7.9568 0.845468 7.11133 1.88867 7.11133C2.93188 7.11133 3.77734 7.9568 3.77734 9V15C3.77734 16.0432 2.93188 16.8887 1.88867 16.8887C0.845468 16.8887 3.83039e-08 16.0432 0 15ZM0 3V0H3.77734V3C3.77734 4.0432 2.93188 4.88867 1.88867 4.88867C0.845467 4.88867 3.83039e-08 4.0432 0 3Z"
          fill="#E7EBF8"
        />
      </svg>
      <div className="relative flex w-full items-center rounded-lg [border-width:var(--one-px)] border-purple-600 bg-white p-3 shadow-(--shadow-smooth)">
        <div className="relative flex size-12.5 items-center justify-center rounded-full bg-linear-to-b from-purple-600 to-purple-700">
          <NotePencil weight="fill" className="size-6 text-white" />
          <svg
            viewBox="0 0 48 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="absolute inset-0 size-full opacity-30"
          >
            <g
              style={{
                mixBlendMode: 'soft-light',
              }}
            >
              <path
                d="M-5.3054 77.9335L25.8325 -6.82949L34.195 -7.10989L4.04091 77.6201L-5.3054 77.9335Z"
                fill="url(#paint0_linear_12794_122814)"
                fill-opacity="0.2"
              />
              <path
                d="M-3.83307 111.098L47.5592 -29.5003L61.367 -29.9632L11.5992 110.58L-3.83307 111.098Z"
                fill="url(#paint1_linear_12794_122814)"
                fill-opacity="0.2"
              />
            </g>
            <defs>
              <linearGradient
                id="paint0_linear_12794_122814"
                x1="13.0428"
                y1="-6.40065"
                x2="15.8468"
                y2="77.2242"
                gradientUnits="userSpaceOnUse"
              >
                <stop offset="0.177885" stop-color="#FBFCFE" />
                <stop offset="0.576923" stop-color="#F6F8FD" />
                <stop offset="1" stop-color="#EFF2FB" />
              </linearGradient>
              <linearGradient
                id="paint1_linear_12794_122814"
                x1="26.4413"
                y1="-28.7922"
                x2="31.0926"
                y2="109.927"
                gradientUnits="userSpaceOnUse"
              >
                <stop offset="0.177885" stop-color="#FBFCFE" />
                <stop offset="0.576923" stop-color="#F6F8FD" />
                <stop offset="1" stop-color="#EFF2FB" />
              </linearGradient>
            </defs>
          </svg>
        </div>
        <div className="ml-3">
          <p className="text-sm font-black">Gesuchsformular</p>
          <p className="text-xs text-gray-600">
            Beantrage deinen Lernfahrausweis
          </p>
        </div>
        <p className="absolute top-1.5 right-2.5 text-2xs text-purple-600">
          Aktueller Schritt
        </p>
      </div>
      <svg
        viewBox="0 0 4 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="h-6 w-1"
      >
        <path
          d="M0 21C-8.20931e-10 19.9568 0.845468 19.1113 1.88867 19.1113C2.93188 19.1113 3.77734 19.9568 3.77734 21V24H0V21ZM0 15V9C-8.20931e-10 7.9568 0.845468 7.11133 1.88867 7.11133C2.93188 7.11133 3.77734 7.9568 3.77734 9V15C3.77734 16.0432 2.93188 16.8887 1.88867 16.8887C0.845468 16.8887 3.83039e-08 16.0432 0 15ZM0 3V0H3.77734V3C3.77734 4.0432 2.93188 4.88867 1.88867 4.88867C0.845467 4.88867 3.83039e-08 4.0432 0 3Z"
          fill="#E7EBF8"
        />
      </svg>
      <div className="w-full px-2">
        <div className="relative flex w-full items-center rounded-lg [border-width:var(--one-px)] border-gray-150 bg-white p-3 opacity-80 shadow-(--shadow-xs)">
          <div className="flex size-11 items-center justify-center rounded-full [border-width:var(--one-px)] border-gray-150 bg-gray-100">
            <DeviceMobile weight="fill" className="size-5 text-gray-600" />
          </div>
          <div className="ml-3">
            <p className="text-sm font-black">Theorieprüfung</p>
            <p className="text-xs text-gray-600">Meistere die Theorieprüfung</p>
          </div>
          <p className="absolute top-1.5 right-2.5 text-2xs text-gray-600">
            Nächster Schritt
          </p>
        </div>
      </div>
    </div>
  )
}

const IllustrationLearningMaterials = () => {
  return (
    <div className="flex flex-col text-base">
      <p className="mx-7.5 mt-8.5 text-sm font-bold">Parkassistent</p>
      <div className="mt-2 flex w-202 items-center self-center">
        <div className="relative h-35 w-62.5 overflow-hidden rounded-lg opacity-30">
          <Image
            src={LearnmaterialLeft}
            alt="Learnmaterial"
            layout="fill"
            sizes="500px"
            className="object-cover"
          />
        </div>
        <div className="relative mx-3 h-40 w-70 overflow-hidden rounded-lg">
          <Image
            src={LearnmaterialMain}
            alt="Parkassistent Video"
            layout="fill"
            sizes="500px"
            className="object-cover"
          />
          <div className="absolute inset-x-0 bottom-0 flex items-center px-2 py-1.5">
            <Pause weight="fill" className="size-3.5 text-white" />
            <div className="ml-1.5 h-0.5 flex-1 rounded-full bg-white/20">
              <div className="h-full w-22 bg-white" />
            </div>
            <p className="ml-3 text-2xs text-white">01:26 / 03:40</p>
          </div>
        </div>
        <div className="relative h-35 w-62.5 overflow-hidden rounded-lg opacity-30">
          <Image
            src={LearnmaterialRight}
            alt="Learnmaterial"
            layout="fill"
            sizes="500px"
            className="object-cover"
          />
        </div>
      </div>
      <div className="relative w-54 -translate-y-0.75 self-center rounded-md [border-width:var(--one-px)] border-gray-100 bg-white px-4 py-3 shadow-(--shadow-smooth)">
        <p className="text-xs">
          Achte immer auch auf die Spiegel und drehe dich um, um wirklich alles
          im Blick zu haben.
        </p>
        <div className="absolute top-[calc(var(--spacing)*0.25/2)] left-20.25 -translate-x-1/2 -translate-y-full">
          <svg
            viewBox="0 0 14 9"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="h-2.25 w-3.5"
          >
            <path
              d="M14 8.5L7.77193 0.937344C7.37192 0.45162 6.62808 0.45162 6.22807 0.937344L-9.53674e-07 8.5L14 8.5Z"
              fill="white"
            />
            <mask id="path-2-inside-1_13022_19887" fill="white">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M7.77148 0.93471L14 8.50012H12.7061L7 1.5707L1.29004 8.50012H0L6.22852 0.93471C6.45508 0.659702 6.79102 0.540027 7.11328 0.576663C7.36035 0.604505 7.59863 0.723692 7.77148 0.93471Z"
              />
            </mask>
            <path
              d="M7.77148 0.93471L6.99788 1.56839L6.99946 1.57031L7.77148 0.93471ZM14 8.50012V9.50012H16.1186L14.772 7.86452L14 8.50012ZM12.7061 8.50012L11.9341 9.13579L12.2341 9.50012H12.7061V8.50012ZM7 1.5707L7.77196 0.935025L7.00026 -0.00212009L6.22825 0.934765L7 1.5707ZM1.29004 8.50012V9.50012H1.76179L2.06178 9.13605L1.29004 8.50012ZM0 8.50012L-0.772021 7.86452L-2.11859 9.50012H0V8.50012ZM6.22852 0.93471L5.4567 0.29886L5.45649 0.299114L6.22852 0.93471ZM7.11328 0.576663L7.00033 1.57026L7.0013 1.57037L7.11328 0.576663ZM7.77148 0.93471L6.99946 1.57031L13.228 9.13571L14 8.50012L14.772 7.86452L8.54351 0.299114L7.77148 0.93471ZM14 8.50012V7.50012H12.7061V8.50012V9.50012H14V8.50012ZM12.7061 8.50012L13.478 7.86444L7.77196 0.935025L7 1.5707L6.22804 2.20637L11.9341 9.13579L12.7061 8.50012ZM7 1.5707L6.22825 0.934765L0.518293 7.86419L1.29004 8.50012L2.06178 9.13605L7.77175 2.20663L7 1.5707ZM1.29004 8.50012V7.50012H0V8.50012V9.50012H1.29004V8.50012ZM0 8.50012L0.772021 9.13571L7.00054 1.57031L6.22852 0.93471L5.45649 0.299114L-0.772021 7.86452L0 8.50012ZM6.22852 0.93471L7.00033 1.57056L7.00033 1.57026L7.11328 0.576663L7.22623 -0.416938C6.58458 -0.489881 5.9093 -0.250515 5.4567 0.29886L6.22852 0.93471ZM7.11328 0.576663L7.0013 1.57037C7.00277 1.57054 7.00228 1.57076 7.00031 1.56971C6.99931 1.56918 6.99835 1.56853 6.99759 1.56792C6.9968 1.56727 6.99693 1.56722 6.99789 1.56839L7.77148 0.93471L8.54508 0.301033C8.19877 -0.121743 7.7181 -0.361509 7.22526 -0.417048L7.11328 0.576663Z"
              fill="#E7EBF8"
              mask="url(#path-2-inside-1_13022_19887)"
            />
          </svg>
        </div>
      </div>
    </div>
  )
}

const IllustrationLearnProgress = () => {
  return (
    <div className="flex flex-col items-center text-base">
      <div className="relative -mt-18 size-60">
        <Image
          src={LearnProgress}
          alt="Lernfortschritt Illustration"
          layout="fill"
          sizes="500px"
          className="object-contain"
        />
      </div>
      <div className="relative mt-3 w-70 rounded-md [border-width:var(--one-px)] border-gray-150 bg-white px-3 py-2 shadow-(--shadow-smooth)">
        <p className="text-xs text-gray-600">Neu im Griff</p>
        <div className="mt-1">
          <div className="flex items-center justify-between py-1.5">
            <div className="flex items-center">
              <div className="flex size-10 items-center justify-center rounded-full [border-width:var(--one-px)] border-green-100 bg-green-50">
                <Trophy weight="fill" className="size-5 text-green-600" />
              </div>
              <div className="ml-2">
                <p className="text-sm font-bold">Notbremsung</p>
                <p className="text-xs text-gray-800">
                  <span>5 Lerninhalte</span>
                  <span className="mx-1">·</span>Im Griff
                </p>
              </div>
            </div>
            <CaretRight weight="bold" className="size-5 text-purple-600" />
          </div>
          <div className="flex items-center justify-between py-1.5">
            <div className="flex items-center">
              <div className="flex size-10 items-center justify-center rounded-full [border-width:var(--one-px)] border-green-100 bg-green-50">
                <Trophy weight="fill" className="size-5 text-green-600" />
              </div>
              <div className="ml-2">
                <p className="text-sm font-bold">Blicktechnik beim Fahren</p>
                <p className="text-xs text-gray-800">
                  <span>4 Lerninhalte</span>
                  <span className="mx-1">·</span>Im Griff
                </p>
              </div>
            </div>
            <CaretRight weight="bold" className="size-5 text-purple-600" />
          </div>
        </div>
        <div className="absolute top-[calc(var(--spacing)*0.25/2)] left-1/2 -translate-x-1/2 -translate-y-full">
          <svg
            viewBox="0 0 14 9"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="h-2.25 w-3.5"
          >
            <path
              d="M14 8.5L7.77193 0.937344C7.37192 0.45162 6.62808 0.45162 6.22807 0.937344L-9.53674e-07 8.5L14 8.5Z"
              fill="white"
            />
            <mask id="path-2-inside-1_13022_19887" fill="white">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M7.77148 0.93471L14 8.50012H12.7061L7 1.5707L1.29004 8.50012H0L6.22852 0.93471C6.45508 0.659702 6.79102 0.540027 7.11328 0.576663C7.36035 0.604505 7.59863 0.723692 7.77148 0.93471Z"
              />
            </mask>
            <path
              d="M7.77148 0.93471L6.99788 1.56839L6.99946 1.57031L7.77148 0.93471ZM14 8.50012V9.50012H16.1186L14.772 7.86452L14 8.50012ZM12.7061 8.50012L11.9341 9.13579L12.2341 9.50012H12.7061V8.50012ZM7 1.5707L7.77196 0.935025L7.00026 -0.00212009L6.22825 0.934765L7 1.5707ZM1.29004 8.50012V9.50012H1.76179L2.06178 9.13605L1.29004 8.50012ZM0 8.50012L-0.772021 7.86452L-2.11859 9.50012H0V8.50012ZM6.22852 0.93471L5.4567 0.29886L5.45649 0.299114L6.22852 0.93471ZM7.11328 0.576663L7.00033 1.57026L7.0013 1.57037L7.11328 0.576663ZM7.77148 0.93471L6.99946 1.57031L13.228 9.13571L14 8.50012L14.772 7.86452L8.54351 0.299114L7.77148 0.93471ZM14 8.50012V7.50012H12.7061V8.50012V9.50012H14V8.50012ZM12.7061 8.50012L13.478 7.86444L7.77196 0.935025L7 1.5707L6.22804 2.20637L11.9341 9.13579L12.7061 8.50012ZM7 1.5707L6.22825 0.934765L0.518293 7.86419L1.29004 8.50012L2.06178 9.13605L7.77175 2.20663L7 1.5707ZM1.29004 8.50012V7.50012H0V8.50012V9.50012H1.29004V8.50012ZM0 8.50012L0.772021 9.13571L7.00054 1.57031L6.22852 0.93471L5.45649 0.299114L-0.772021 7.86452L0 8.50012ZM6.22852 0.93471L7.00033 1.57056L7.00033 1.57026L7.11328 0.576663L7.22623 -0.416938C6.58458 -0.489881 5.9093 -0.250515 5.4567 0.29886L6.22852 0.93471ZM7.11328 0.576663L7.0013 1.57037C7.00277 1.57054 7.00228 1.57076 7.00031 1.56971C6.99931 1.56918 6.99835 1.56853 6.99759 1.56792C6.9968 1.56727 6.99693 1.56722 6.99789 1.56839L7.77148 0.93471L8.54508 0.301033C8.19877 -0.121743 7.7181 -0.361509 7.22526 -0.417048L7.11328 0.576663Z"
              fill="#E7EBF8"
              mask="url(#path-2-inside-1_13022_19887)"
            />
          </svg>
        </div>
      </div>
    </div>
  )
}

const IllustrationChooseInstructor = (
  instructors: Mandatory<Website.TeamMember, 'image'>[]
) => {
  return (
    <div className="-ml-52.25 flex h-full flex-col justify-center text-base">
      <div className="ml-19 flex space-x-3">
        <div className="flex min-w-60 shrink-0 justify-between rounded-lg [border-width:var(--one-px)] border-gray-300 bg-white p-2 opacity-60 shadow-(--shadow-xs)">
          <div className="flex items-center space-x-3 py-1">
            <div className="size-11" />
            <p className="mt-5 text-xs text-gray-600">
              Auto & Motorrad Fahrlehrerin
            </p>
          </div>
          <div>
            <div className="size-5 rounded-full [border-width:var(--one-px)] border-gray-300 bg-white" />
          </div>
        </div>
        <div className="flex min-w-60 shrink-0 justify-between rounded-lg [border-width:var(--one-px)] border-gray-300 bg-white p-2 shadow-(--shadow-xs)">
          <div className="flex items-center space-x-3 py-1">
            <div className="relative size-11 shrink-0">
              <Picture
                permalink={instructors[1].image}
                alt={instructors[1].name}
                fill
                sizes="200px"
                className="rounded-full object-cover object-top"
              />
            </div>
            <div>
              <p className="text-sm font-black">{instructors[1].name}</p>
              <p className="text-xs whitespace-nowrap text-gray-600">
                {instructors[1].short_description}
              </p>
            </div>
          </div>
          <div>
            <div className="size-5 rounded-full [border-width:var(--one-px)] border-gray-300 bg-white" />
          </div>
        </div>
      </div>
      <div className="mt-2.5 flex items-center space-x-3">
        <div className="flex min-w-60 shrink-0 justify-between rounded-lg [border-width:var(--one-px)] border-gray-300 bg-white p-2 opacity-60 shadow-(--shadow-xs)">
          <div className="flex items-center space-x-3 py-1">
            <div className="size-11" />
            <p className="mt-5 text-xs text-gray-600">
              Auto & Motorrad Fahrlehrer
            </p>
          </div>
          <div>
            <div className="size-5 rounded-full [border-width:var(--one-px)] border-gray-300 bg-white" />
          </div>
        </div>
        <div className="flex min-w-60 shrink-0 justify-between rounded-lg [border-width:var(--one-px)] border-purple-600 bg-white p-2 shadow-(--shadow-smooth)">
          <div className="flex items-center space-x-3 py-1">
            <div className="relative size-12 shrink-0">
              <Picture
                permalink={instructors[0].image}
                alt={instructors[0].name}
                fill
                sizes="200px"
                className="rounded-full object-cover object-top"
              />
            </div>
            <div>
              <p className="text-sm font-black">{instructors[0].name}</p>
              <p className="text-xs whitespace-nowrap text-gray-600">
                {instructors[0].short_description}
              </p>
            </div>
          </div>
          <div>
            <div className="flex size-5 items-center justify-center rounded-full bg-purple-600">
              <CheckIcon className="size-3 text-white" />
            </div>
          </div>
        </div>
        <div className="flex min-w-60 shrink-0 justify-between rounded-lg [border-width:var(--one-px)] border-gray-300 bg-white p-2 shadow-(--shadow-xs)">
          <div className="flex items-center space-x-3 py-1">
            <div className="relative size-11 shrink-0">
              <Picture
                permalink={instructors[3].image}
                alt={instructors[3].name}
                fill
                sizes="200px"
                className="rounded-full object-cover object-top"
              />
            </div>
            <div>
              <p className="text-sm font-black">{instructors[3].name}</p>
              <p className="text-xs whitespace-nowrap text-gray-600">
                {instructors[3].short_description}
              </p>
            </div>
          </div>
          <div>
            <div className="size-5 rounded-full [border-width:var(--one-px)] border-gray-300 bg-white" />
          </div>
        </div>
      </div>
      <div className="mt-2.5 ml-43.75 flex space-x-3">
        <div className="flex min-w-60 shrink-0 justify-between rounded-lg [border-width:var(--one-px)] border-gray-300 bg-white p-2 shadow-(--shadow-xs)">
          <div className="flex items-center space-x-3 py-1">
            <div className="relative size-11 shrink-0">
              <Picture
                permalink={instructors[2].image}
                alt={instructors[2].name}
                fill
                sizes="200px"
                className="rounded-full object-cover object-top"
              />
            </div>
            <div>
              <p className="text-sm font-black">{instructors[2].name}</p>
              <p className="text-xs whitespace-nowrap text-gray-600">
                {instructors[2].short_description}
              </p>
            </div>
          </div>
          <div>
            <div className="size-5 rounded-full [border-width:var(--one-px)] border-gray-300 bg-white" />
          </div>
        </div>
        <div className="flex min-w-60 shrink-0 justify-between rounded-lg [border-width:var(--one-px)] border-gray-300 bg-white p-2 shadow-(--shadow-xs)">
          <div className="flex items-center space-x-3 py-1">
            <div className="relative size-11 shrink-0">
              <Picture
                permalink={instructors[4].image}
                alt={instructors[4].name}
                fill
                sizes="200px"
                className="rounded-full object-cover object-top"
              />
            </div>
            <div>
              <p className="text-sm font-black">{instructors[4].name}</p>
              <p className="text-xs whitespace-nowrap text-gray-600">
                {instructors[4].short_description}
              </p>
            </div>
          </div>
          <div>
            <div className="size-5 rounded-full [border-width:var(--one-px)] border-gray-300 bg-white" />
          </div>
        </div>
      </div>
      <div className="mt-2.5 ml-21.25 flex space-x-3">
        <div className="flex min-w-60 shrink-0 justify-between rounded-lg [border-width:var(--one-px)] border-gray-300 bg-white p-2 opacity-60 shadow-(--shadow-xs)">
          <div className="flex items-center space-x-3 py-1">
            <div className="size-11" />
            <p className="mt-5 text-xs text-gray-600">
              Auto & Motorrad Fahrlehrer
            </p>
          </div>
          <div>
            <div className="size-5 rounded-full [border-width:var(--one-px)] border-gray-300 bg-white" />
          </div>
        </div>
        <div className="flex min-w-60 shrink-0 justify-between rounded-lg [border-width:var(--one-px)] border-gray-300 bg-white p-2 shadow-(--shadow-xs)">
          <div className="flex items-center space-x-3 py-1">
            <div className="relative size-11 shrink-0">
              <Picture
                permalink={instructors[5].image}
                alt={instructors[5].name}
                fill
                sizes="200px"
                className="rounded-full object-cover object-top"
              />
            </div>
            <div>
              <p className="text-sm font-black">{instructors[5].name}</p>
              <p className="text-xs whitespace-nowrap text-gray-600">
                {instructors[5].short_description}
              </p>
            </div>
          </div>
          <div>
            <div className="size-5 rounded-full [border-width:var(--one-px)] border-gray-300 bg-white" />
          </div>
        </div>
      </div>
    </div>
  )
}
